<template>
  <div class="min-h-screen flex flex-col bg-zinc-50 text-zinc-900 dark:bg-zinc-950 dark:text-zinc-50">
    <div class="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
      <div class="absolute -top-32 -left-32 h-72 w-72 rounded-full bg-sky-400/15 blur-3xl" />
      <div class="absolute -bottom-40 -right-20 h-80 w-80 rounded-full bg-cyan-500/15 blur-3xl" />
    </div>

    <div class="flex-1 pb-24">
      <header
        class="w-full border-b border-black/5 bg-white/80 dark:border-white/10 dark:bg-[#0a0c10]/80 backdrop-blur-xl"
      >
        <div
          class="max-w-7xl mx-auto px-4 sm:px-6 py-3.5 sm:py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4"
        >
          <div class="flex flex-wrap items-center gap-2.5 sm:gap-3">
            <router-link
              :to="{ path: '/' }"
              class="inline-flex items-center gap-1.5 rounded-2xl border border-zinc-200/80 dark:border-zinc-800/80 bg-zinc-100/70 dark:bg-zinc-900/70 px-2.5 py-1.5 text-[11px] font-medium text-zinc-600 dark:text-zinc-300 hover:bg-zinc-200/80 dark:hover:bg-zinc-800/80 transition-colors"
            >
              <span class="material-symbols-rounded text-[16px]">arrow_back</span>
              Home
            </router-link>

            <button
              type="button"
              @click="toggleTheme"
              class="inline-flex items-center gap-1.5 rounded-2xl border border-zinc-200/80 dark:border-zinc-800/80 bg-zinc-100/70 dark:bg-zinc-900/70 px-2.5 py-1.5 text-[11px] font-medium text-zinc-600 dark:text-zinc-300 hover:bg-zinc-200/80 dark:hover:bg-zinc-800/80 transition-colors"
            >
              <span class="material-symbols-rounded text-[16px]">
                {{ isDark ? 'dark_mode' : 'light_mode' }}
              </span>
              <span>{{ isDark ? 'Dark' : 'Light' }}</span>
            </button>

            <div class="flex items-center gap-3 min-w-0">
              <div
                class="flex h-10 w-10 items-center justify-center rounded-2xl bg-gradient-to-br from-sky-500 via-sky-400 to-cyan-400 shadow-[0_8px_22px_rgba(56,189,248,0.45)] ring-1 ring-sky-500/40"
              >
                <img :src="logo" class="h-7 w-7 rounded-xl object-cover" />
              </div>

              <div class="leading-tight">
                <p class="text-[11px] uppercase tracking-[0.18em] text-zinc-500 dark:text-zinc-400">
                  NexGridCo
                </p>
                <p class="text-sm font-medium text-zinc-900 dark:text-zinc-50">
                  Palette Studio · Documentation
                </p>
              </div>
            </div>
          </div>

          <div
            class="inline-flex items-center gap-2 rounded-full border border-zinc-200/80 dark:border-zinc-800/80 bg-zinc-100/70 dark:bg-zinc-900/70 px-3 py-1 w-fit"
          >
            <span class="h-1.5 w-1.5 rounded-full bg-emerald-500" />
            <span class="text-[11px] font-medium uppercase tracking-[0.18em] text-zinc-500 dark:text-zinc-400">
              Docs updated
            </span>
          </div>
        </div>
      </header>

      <UiSection>
        <div class="w-full max-w-6xl mx-auto px-4 sm:px-6">
          <div class="pt-7 sm:pt-9 pb-7 space-y-2 sm:space-y-3">
            <p class="text-[11px] uppercase tracking-[0.20em] text-zinc-500 dark:text-zinc-400">
              Guides & Resources
            </p>

            <h1 class="text-[22px] sm:text-3xl font-semibold tracking-tight">
              Explore every section of your workspace
            </h1>

            <p class="text-sm sm:text-[15px] text-zinc-600 dark:text-zinc-400 max-w-2xl">
              Each section below mirrors the left sidebar. Use this page to write how you like to work and embed a video
              under each area with a full walkthrough.
            </p>
          </div>

          <div
            class="grid gap-8 sm:gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(240px,260px)] lg:items-start"
          >
            <main class="space-y-8 sm:space-y-10">
              <section id="dashboard" class="space-y-4 border-b border-zinc-200/70 dark:border-zinc-800/70 pb-8">
                <h2 class="text-lg sm:text-xl font-semibold tracking-tight">
                  Dashboard
                </h2>

                <p class="text-sm sm:text-[15px] text-zinc-600 dark:text-zinc-300 leading-relaxed">
                  The dashboard is your starting point. Here you can highlight what matters most: quick links to
                  projects, recent palettes, pinned explorations and workspace status. Use this space to explain what
                  people should pay attention to the moment they log in.
                </p>

                <ul class="list-disc list-inside text-sm text-zinc-700 dark:text-zinc-200 space-y-1.5">
                  <li>What each main section of the dashboard represents.</li>
                  <li>How to jump quickly to Palette Studio, AI tools or Library.</li>
                  <li>Your suggested “first actions” when opening the app.</li>
                </ul>

                <div class="doc-video mt-4">
                  <span class="material-symbols-rounded text-2xl mb-1">play_circle</span>
                  <p class="text-[12px] font-medium">Dashboard overview video</p>
                  <p class="text-[11px] text-zinc-400 dark:text-zinc-500 max-w-sm mx-auto">
                    Embed your screen recording here showing the main layout and shortcuts.
                  </p>
                </div>
              </section>

              <section id="generate-ai" class="space-y-4 border-b border-zinc-200/70 dark:border-zinc-800/70 pb-8">
                <h2 class="text-lg sm:text-xl font-semibold tracking-tight">
                  Generate With AI
                </h2>

                <p class="text-sm sm:text-[15px] text-zinc-600 dark:text-zinc-300 leading-relaxed">
                  Generate With AI is where you brief the system to create palettes, gradients or layouts from scratch.
                  Use this section to document how to write strong prompts, what kind of inputs you like to give
                  (brand, mood, audience) and how you refine the results.
                </p>

                <ul class="list-disc list-inside text-sm text-zinc-700 dark:text-zinc-200 space-y-1.5">
                  <li>Examples of prompts that worked well for you.</li>
                  <li>How to ask for alternatives, variations or small tweaks.</li>
                  <li>How generated palettes are saved and connected to projects.</li>
                </ul>

                <div class="doc-video mt-4">
                  <span class="material-symbols-rounded text-2xl mb-1">play_circle</span>
                  <p class="text-[12px] font-medium">Generate With AI walkthrough</p>
                  <p class="text-[11px] text-zinc-400 dark:text-zinc-500 max-w-sm mx-auto">
                    Show a real prompt → result flow and how you refine it.
                  </p>
                </div>
              </section>

              <section id="ai-chat" class="space-y-4 border-b border-zinc-200/70 dark:border-zinc-800/70 pb-8">
                <h2 class="text-lg sm:text-xl font-semibold tracking-tight">
                  NexGrid AI Chat
                </h2>

                <p class="text-sm sm:text-[15px] text-zinc-600 dark:text-zinc-300 leading-relaxed">
                  NexGrid AI Chat é o lugar para conversar sobre decisões de cor, acessibilidade e UX em linguagem
                  natural. Aqui você pode explicar como usa o chat para validar ideias, revisar contrastes ou pedir
                  sugestões rápidas sem precisar gerar uma paleta nova.
                </p>

                <ul class="list-disc list-inside text-sm text-zinc-700 dark:text-zinc-200 space-y-1.5">
                  <li>Quando faz sentido abrir o chat em vez de usar o gerador.</li>
                  <li>Exemplos de perguntas sobre contraste, branding, estados de UI.</li>
                  <li>Como o chat pode se conectar às paletas e tokens já existentes.</li>
                </ul>

                <div class="doc-video mt-4">
                  <span class="material-symbols-rounded text-2xl mb-1">play_circle</span>
                  <p class="text-[12px] font-medium">AI Chat examples</p>
                  <p class="text-[11px] text-zinc-400 dark:text-zinc-500 max-w-sm mx-auto">
                    Mostre algumas conversas reais e como você chega em decisões de cor com o assistente.
                  </p>
                </div>
              </section>

              <section id="palette-studio" class="space-y-4 border-b border-zinc-200/70 dark:border-zinc-800/70 pb-8">
                <h2 class="text-lg sm:text-xl font-semibold tracking-tight">
                  Palette Studio
                </h2>

                <p class="text-sm sm:text-[15px] text-zinc-600 dark:text-zinc-300 leading-relaxed">
                  Palette Studio is the core of the product. Use this area to explicar como você constrói, nomeia e
                  testa sistemas de cor completos. Aqui você pode detalhar seu fluxo de criação de paleta base, ajustes
                  finos e preview em diferentes superfícies.
                </p>

                <p class="text-sm sm:text-[15px] text-zinc-600 dark:text-zinc-300 leading-relaxed">
                  Você também pode documentar seu sistema de papéis semânticos: quais cores viram surface-base,
                  surface-elevated, accent, accent-subtle, text-high, text-muted, borders, success, warning, error e por
                  aí vai.
                </p>

                <div class="doc-video mt-4">
                  <span class="material-symbols-rounded text-2xl mb-1">play_circle</span>
                  <p class="text-[12px] font-medium">Palette Studio deep dive</p>
                  <p class="text-[11px] text-zinc-400 dark:text-zinc-500 max-w-sm mx-auto">
                    Grave um vídeo longo mostrando a criação de uma paleta do zero até a exportação.
                  </p>
                </div>
              </section>

              <section id="sky-studio" class="space-y-4 border-b border-zinc-200/70 dark:border-zinc-800/70 pb-8">
                <h2 class="text-lg sm:text-xl font-semibold tracking-tight">
                  Sky Studio
                </h2>

                <p class="text-sm sm:text-[15px] text-zinc-600 dark:text-zinc-300 leading-relaxed">
                  Sky Studio é o espaço para fundos e gradientes inspirados em céu, neblina, aurora e luz ambiente. Aqui
                  você pode explicar como escolhe paletas base, como testa combinações com tipografia e como decide a
                  intensidade ideal para fundos de hero, capas ou thumbnails.
                </p>

                <div class="doc-video mt-4">
                  <span class="material-symbols-rounded text-2xl mb-1">play_circle</span>
                  <p class="text-[12px] font-medium">Sky Studio in practice</p>
                  <p class="text-[11px] text-zinc-400 dark:text-zinc-500 max-w-sm mx-auto">
                    Mostre a criação de alguns fundos e onde você usaria cada um.
                  </p>
                </div>
              </section>

              <section id="gradient-craft" class="space-y-4 border-b border-zinc-200/70 dark:border-zinc-800/70 pb-8">
                <h2 class="text-lg sm:text-xl font-semibold tracking-tight">
                  Gradient Craft
                </h2>

                <p class="text-sm sm:text-[15px] text-zinc-600 dark:text-zinc-300 leading-relaxed">
                  Gradient Craft foca em controle fino: ângulos, stops, intensidade, ruído e presets reutilizáveis. Use
                  este trecho para registrar seus padrões preferidos, combinações que funcionam bem em UI e limites que
                  você evita (tipo gradiente muito forte em áreas de leitura).
                </p>

                <div class="doc-video mt-4">
                  <span class="material-symbols-rounded text-2xl mb-1">play_circle</span>
                  <p class="text-[12px] font-medium">Gradient Craft tour</p>
                  <p class="text-[11px] text-zinc-400 dark:text-zinc-500 max-w-sm mx-auto">
                    Demonstre como ajustar stops, salvar presets e exportar para CSS ou Figma.
                  </p>
                </div>
              </section>

              <section
                id="accessibility-lab"
                class="space-y-4 border-b border-zinc-200/70 dark:border-zinc-800/70 pb-8"
              >
                <h2 class="text-lg sm:text-xl font-semibold tracking-tight">
                  Accessibility Lab
                </h2>

                <p class="text-sm sm:text-[15px] text-zinc-600 dark:text-zinc-300 leading-relaxed">
                  Accessibility Lab é onde você garante que a paleta aguenta o uso real. Explique como testar pares de
                  texto e fundo, como interpretar os níveis AA e AAA e quais ajustes você costuma fazer quando uma cor
                  quase passa no teste.
                </p>

                <div class="doc-video mt-4">
                  <span class="material-symbols-rounded text-2xl mb-1">play_circle</span>
                  <p class="text-[12px] font-medium">Accessibility workflow</p>
                  <p class="text-[11px] text-zinc-400 dark:text-zinc-500 max-w-sm mx-auto">
                    Mostre um caso real: combinação que não passa, ajuste e resultado final.
                  </p>
                </div>
              </section>

              <section id="token-studio" class="space-y-4 border-b border-zinc-200/70 dark:border-zinc-800/70 pb-8">
                <h2 class="text-lg sm:text-xl font-semibold tracking-tight">
                  Token Studio
                </h2>

                <p class="text-sm sm:text-[15px] text-zinc-600 dark:text-zinc-300 leading-relaxed">
                  Token Studio é a ponte entre design e código. Aqui você documenta como mapeia cores em tokens
                  semânticos, como organiza grupos (core, functional, states) e quais formatos de export usa no dia a
                  dia.
                </p>

                <ul class="list-disc list-inside text-sm text-zinc-700 dark:text-zinc-200 space-y-1.5">
                  <li>Como você nomeia tokens para evitar ambiguidade.</li>
                  <li>Quais exports usa (JSON, CSS, Tailwind, Figma Tokens etc.).</li>
                  <li>Como lida com versões de tokens por projeto ou marca.</li>
                </ul>

                <div class="doc-video mt-4">
                  <span class="material-symbols-rounded text-2xl mb-1">play_circle</span>
                  <p class="text-[12px] font-medium">Token Studio overview</p>
                  <p class="text-[11px] text-zinc-400 dark:text-zinc-500 max-w-sm mx-auto">
                    Grave o fluxo completo: paleta → tokens → export sendo usados em um projeto.
                  </p>
                </div>
              </section>

              <section id="ui-playground" class="space-y-4 border-b border-zinc-200/70 dark:border-zinc-800/70 pb-8">
                <h2 class="text-lg sm:text-xl font-semibold tracking-tight">
                  UI Playground
                </h2>

                <p class="text-sm sm:text-[15px] text-zinc-600 dark:text-zinc-300 leading-relaxed">
                  UI Playground é a área de teste. Use este texto para explicar como você liga componentes às paletas
                  e tokens, como compara versões diferentes e como usa o playground em revisões com clientes ou equipe.
                </p>

                <div class="doc-video mt-4">
                  <span class="material-symbols-rounded text-2xl mb-1">play_circle</span>
                  <p class="text-[12px] font-medium">UI Playground session</p>
                  <p class="text-[11px] text-zinc-400 dark:text-zinc-500 max-w-sm mx-auto">
                    Mostre um layout completo trocando de paleta ao vivo para comparar resultados.
                  </p>
                </div>
              </section>

              <section id="library" class="space-y-4 pb-4">
                <h2 class="text-lg sm:text-xl font-semibold tracking-tight">
                  Library, folders & support
                </h2>

                <p class="text-sm sm:text-[15px] text-zinc-600 dark:text-zinc-300 leading-relaxed">
                  A Library guarda tudo: paletas, gradientes, exports e cenas que você quer revisitar. Use esta parte
                  para explicar como organiza por cliente, projeto, plataforma ou vibe, e como as pastas ajudam a
                  manter tudo navegável.
                </p>

                <p class="text-sm sm:text-[15px] text-zinc-600 dark:text-zinc-300 leading-relaxed">
                  Também é um bom lugar para explicar como o botão de Support funciona: canais preferidos, tempos
                  médios de resposta e que tipo de informação ajuda você a resolver dúvidas rápido (prints, links de
                  projeto, descrição do contexto).
                </p>

                <div class="doc-video mt-4">
                  <span class="material-symbols-rounded text-2xl mb-1">play_circle</span>
                  <p class="text-[12px] font-medium">Library & support overview</p>
                  <p class="text-[11px] text-zinc-400 dark:text-zinc-500 max-w-sm mx-auto">
                    Mostre como organiza as pastas, como encontrar assets e como o usuário pode pedir ajuda.
                  </p>
                </div>
              </section>
            </main>

            <aside class="space-y-4">
              <div
                class="rounded-3xl border border-black/5 dark:border-white/10 bg-white/95 dark:bg-zinc-900/90 backdrop-blur-xl px-4 py-4 shadow-[0_12px_40px_-18px_rgba(15,23,42,0.6)] sticky top-20 sm:top-24 max-h-[70vh] overflow-y-auto"
              >
                <p class="text-[11px] uppercase font-semibold tracking-[0.18em] text-zinc-500 dark:text-zinc-400 mb-2">
                  On this page
                </p>

                <nav class="space-y-1 text-sm">
                  <a href="#dashboard" class="link-nav-docs">Dashboard</a>
                  <a href="#generate-ai" class="link-nav-docs">Generate With AI</a>
                  <a href="#ai-chat" class="link-nav-docs">NexGrid AI Chat</a>
                  <a href="#palette-studio" class="link-nav-docs">Palette Studio</a>
                  <a href="#sky-studio" class="link-nav-docs">Sky Studio</a>
                  <a href="#gradient-craft" class="link-nav-docs">Gradient Craft</a>
                  <a href="#accessibility-lab" class="link-nav-docs">Accessibility Lab</a>
                  <a href="#token-studio" class="link-nav-docs">Token Studio</a>
                  <a href="#ui-playground" class="link-nav-docs">UI Playground</a>
                  <a href="#library" class="link-nav-docs">Library, folders & support</a>
                </nav>
              </div>
            </aside>
          </div>
        </div>
      </UiSection>
    </div>

    <button v-if="showBackToTop" type="button" class="back-to-top" @click="scrollToTop">
      <span class="material-symbols-rounded text-[20px]">arrow_upward</span>
    </button>

    <FooterBar />
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue'
import logo from '@/assets/logo.png'
import UiSection from '@/components/ui/Section.vue'
import FooterBar from '../marketing/FooterBar.vue'

const showBackToTop = ref(false)
const isDark = ref(false)

const handleScroll = () => {
  showBackToTop.value = window.scrollY > 300
}

const scrollToTop = () => {
  window.scrollTo({ top: 0, behavior: 'smooth' })
}

const toggleTheme = () => {
  isDark.value = !isDark.value
  document.documentElement.classList.toggle('dark', isDark.value)
  localStorage.setItem('theme', isDark.value ? 'dark' : 'light')
}

onMounted(() => {
  const saved = localStorage.getItem('theme')
  if (saved) {
    isDark.value = saved === 'dark'
  } else {
    isDark.value = window.matchMedia('(prefers-color-scheme: dark)').matches
  }
  document.documentElement.classList.toggle('dark', isDark.value)

  window.addEventListener('scroll', handleScroll, { passive: true })
  handleScroll()
})

onBeforeUnmount(() => {
  window.removeEventListener('scroll', handleScroll)
})
</script>

<style scoped>
.link-nav-docs {
  @apply block rounded-lg px-2 py-1.5 text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100/70 dark:text-zinc-300 dark:hover:text-zinc-50 dark:hover:bg-zinc-800/70 text-sm;
}

.doc-video {
  @apply w-full aspect-video max-w-full rounded-3xl border border-zinc-900/40 dark:border-zinc-100/20 bg-zinc-950 text-zinc-50 flex flex-col items-center justify-center text-center px-4;
}

.back-to-top {
  position: fixed;
  right: 1.25rem;
  bottom: 1.75rem;
  z-index: 60;
  height: 44px;
  width: 44px;
  border-radius: 9999px;
  border: 1px solid rgba(148, 163, 184, 0.6);
  background: radial-gradient(circle at 0% 0%, #e0f2fe, #0ea5e9);
  color: #0b1120;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 16px 40px rgba(15, 23, 42, 0.35);
  cursor: pointer;
  transition: transform 0.18s ease-out, box-shadow 0.18s ease-out, filter 0.18s ease-out, opacity 0.18s ease-out;
}

.back-to-top:hover {
  transform: translateY(-1px);
  box-shadow: 0 20px 50px rgba(15, 23, 42, 0.4);
  filter: brightness(1.03);
}

.material-symbols-rounded {
  font-family: 'Material Symbols Rounded';
}
</style>
